import React, { useEffect, useRef } from 'react';
import { MapPin, Clock, Calendar, Ticket, Lock, X } from 'lucide-react';
import { format } from 'date-fns';
import { CalendarEvent } from '../../types/calendar';
import { decodeHtmlEntities, createSafeHtml } from '../../utils/security';

interface EventTooltipProps {
  event: CalendarEvent;
  showLocation?: boolean;
  onClose?: () => void;
  ticketLimit?: number | null;
  remainingTickets?: number | null;
  isPaid?: boolean;
  isSubscribed?: boolean;
  isMobile?: boolean;
}

export default function EventTooltip({
  event,
  showLocation,
  onClose,
  ticketLimit,
  remainingTickets,
  isPaid = false,
  isSubscribed = false,
  isMobile = false
}: EventTooltipProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  const descLocation = event.description?.includes('Location:')
    ? event.description.split('Location:')[1].trim().split('\n')[0]
    : '';
  const location = event.location || descLocation;
  const hasLocation = !!location;

  const cleanedDescription = event.description
    ? event.description
        .split('\n')
        .filter(line => {
          const trimmed = line.trim();
          return !trimmed.startsWith('Timezone:') && !trimmed.startsWith('Location:');
        })
        .join('\n')
        .trim()
    : undefined;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    if (isMobile) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobile, onClose]);

  const isSameDay = format(event.start, 'yyyy-MM-dd') === format(event.end, 'yyyy-MM-dd');
  const dateRangeText = isSameDay
    ? `${format(event.start, 'MMMM d, yyyy')}`
    : `${format(event.start, 'MMMM d')} - ${format(event.end, 'MMMM d, yyyy')}`;

  const timeRangeText = event.allDay ? 'All Day' : `${format(event.start, 'h:mm a')} - ${format(event.end, 'h:mm a')}`;

  const tooltipContent = (
    <>
      <h3 className="text-lg font-semibold mb-3 pr-8">{decodeHtmlEntities(event.title)}</h3>

      <div className="space-y-2 mb-4">
        <div className="flex items-center text-gray-300">
          <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
          <span>{dateRangeText}</span>
        </div>
        <div className="flex items-center text-gray-300">
          <Clock className="h-4 w-4 mr-2 flex-shrink-0" />
          <span>{timeRangeText}</span>
        </div>
        {ticketLimit && (
          <div className="flex items-center text-gray-300">
            <Ticket className="h-4 w-4 mr-2 flex-shrink-0" />
            <span>
              {remainingTickets && remainingTickets > 0 ? (
                remainingTickets === 1 ? (
                  <span className="text-red-400">Only 1 ticket remaining!</span>
                ) : (
                  `${remainingTickets} tickets remaining`
                )
              ) : (
                <span className="text-red-400">Sold Out</span>
              )}
            </span>
          </div>
        )}
      </div>

      {isPaid && !isSubscribed ? (
        <div className="flex items-center text-gray-300 mt-4">
          <Lock className="h-4 w-4 mr-2 flex-shrink-0" />
          <span>Subscribe to view event details</span>
        </div>
      ) : (
        <>
          {cleanedDescription && (
            <div className="text-sm text-gray-200 whitespace-pre-wrap break-words leading-relaxed mb-4">
              {createSafeHtml(cleanedDescription)}
            </div>
          )}

          {hasLocation && showLocation && (
            <div className="mt-4 flex items-start text-sm text-gray-200">
              <MapPin className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
              <span>{decodeHtmlEntities(location)}</span>
            </div>
          )}
        </>
      )}

      {event.calendarName && (
        <div className="mt-4 pt-4 border-t border-gray-700 text-sm text-gray-400">
          <p className="font-medium">{decodeHtmlEntities(event.calendarName)}</p>
          {event.creatorName && (
            <p className="mt-1 opacity-75">by {decodeHtmlEntities(event.creatorName)}</p>
          )}
        </div>
      )}
    </>
  );

  if (isMobile) {
    return (
      <div
        className="fixed inset-0 z-50 flex flex-col justify-end animate-modal-backdrop"
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
      >
        <div
          className="absolute inset-0 bg-black/60"
          onClick={onClose}
          onTouchEnd={(e) => { e.preventDefault(); onClose?.(); }}
        />
        <div
          ref={modalRef}
          className="relative bg-gray-900 text-white rounded-t-2xl shadow-xl overflow-y-auto overscroll-contain animate-sheet-slide-up"
          style={{ maxHeight: '85dvh' }}
        >
          <div className="sticky top-0 bg-gray-900 pt-3 pb-2 flex justify-center z-10">
            <div className="w-10 h-1 rounded-full bg-gray-600" />
          </div>
          <button
            onClick={onClose}
            className="absolute top-3 right-4 p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-gray-700 transition-colors z-20"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="px-6 pb-8">
            {tooltipContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-modal-backdrop"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        className="relative bg-gray-900 text-white rounded-2xl shadow-2xl overflow-y-auto overscroll-contain animate-modal-scale-in"
        style={{
          width: 'min(calc(100vw - 32px), 440px)',
          maxHeight: 'min(calc(100dvh - 32px), 600px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-gray-900 pt-5 px-6 pb-3 z-10 flex items-center justify-between border-b border-gray-800">
          <div className="w-8 h-1 rounded-full bg-gray-700 mx-auto" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-5 pb-7">
          {tooltipContent}
        </div>
      </div>
    </div>
  );
}
